import { useCallback } from 'react'
import { getCommentType } from '@constants'
import { useCommentMutations } from '@scene'
import { useCommentsStore } from '@editorStores'

/**
 * Returns a callback that mirrors an element's Positionsliste text into its linked Positionsplan
 * comment (that field is the single source of truth for a linked comment's text). Called when the
 * text is saved — the detail-pane submit and the element list's position-grouping autosave — not
 * on field blur. The linked comment is read from the store at call time to avoid stale closures.
 * An empty text isn't synced — the comment is removed when the Bemessung is saved (see
 * useReconcilePlanComment). No-op when the element has no linked comment or the text is unchanged.
 */
const useSyncLinkedPlanCommentText = (): ((
  elementGuid: string | undefined,
  text: string,
) => void) => {
  const { updateCommentFields } = useCommentMutations()

  return useCallback(
    (elementGuid: string | undefined, text: string) => {
      const trimmed = text.trim()
      if (!elementGuid || !trimmed) return
      const linked = useCommentsStore
        .getState()
        .comments.find(
          comment =>
            comment.element_guid === elementGuid && getCommentType(comment) === 'position-plan',
        )
      if (linked && linked.text !== trimmed) updateCommentFields(linked.id, { text: trimmed })
    },
    [updateCommentFields],
  )
}

export default useSyncLinkedPlanCommentText
